<template>
  <b-modal
    ref="modal"
    :id="modalId"
    title="Choose from gallery"
    size="lg"
    body-class="p-0"
    centered
    @show="onShow"
    @shown="restoreScrollPosition"
    @hidden="onHidden"
  >
    <div class="gallery-body">
      <div v-if="loading" class="gallery-loading">
        <b-spinner variant="primary" />
      </div>
      <div v-else-if="images.length === 0" class="gallery-empty">No images available.</div>
      <div v-else class="gallery-grid" :class="{ 'gallery-grid--open-banner': isOpenBanner }">
        <button
          v-for="image in images"
          :key="image.s3Key"
          type="button"
          :class="[
            'gallery-item',
            { 'gallery-item--selected': isSelected(image.s3Key), 'gallery-item--open-banner': isOpenBanner },
          ]"
          :disabled="!isSelected(image.s3Key) && selectedS3Keys.length >= maxSelectable"
          @click="toggle(image.s3Key)"
        >
          <img :src="image.url" :alt="image.filename" class="gallery-item__image" />
          <span v-if="isSelected(image.s3Key)" class="gallery-item__tick">
            <font-awesome-icon :icon="['far', 'check']" />
          </span>
        </button>
      </div>
    </div>

    <template #modal-footer>
      <small v-if="atSelectionLimit" class="text-danger mr-auto">You can add up to {{ maxSelectable }} images.</small>
      <b-button variant="primary" :disabled="selectedS3Keys.length === 0" @click="onConfirm">
        {{ confirmLabel }}
      </b-button>
    </template>
  </b-modal>
</template>

<script setup>
import { ref, computed } from 'vue';
import ApiClient from '@/ApiClient';

const props = defineProps({
  modalId: { type: String, required: true },
  imageType: {
    type: String,
    required: true,
    validator: v => ['banner', 'open_banner', 'logo'].includes(v),
  },
  multiple: { type: Boolean, default: false },
  maxSelectable: { type: Number, default: Infinity },
});

const emit = defineEmits(['select']);

const isOpenBanner = computed(() => props.imageType === 'open_banner');

const modal = ref(null);
const loading = ref(false);
const images = ref([]);
const selectedS3Keys = ref([]);

const confirmLabel = computed(() =>
  props.multiple && selectedS3Keys.value.length > 1 ? `Use ${selectedS3Keys.value.length} images` : 'Use image',
);

const atSelectionLimit = computed(() => props.multiple && selectedS3Keys.value.length >= props.maxSelectable);

const isSelected = s3Key => selectedS3Keys.value.includes(s3Key);

const toggle = s3Key => {
  if (props.multiple) {
    selectedS3Keys.value = isSelected(s3Key)
      ? selectedS3Keys.value.filter(k => k !== s3Key)
      : [...selectedS3Keys.value, s3Key];
  } else {
    selectedS3Keys.value = [s3Key];
  }
};

const toImages = entries =>
  Object.entries(entries || {}).map(([s3Key, entry]) => ({
    filename: entry.filename,
    url: entry.url,
    s3Key,
  }));

let scrollY = 0;
const restoreScrollPosition = () => {
  window.scrollTo(0, scrollY);
};

const onShow = async () => {
  scrollY = window.scrollY;
  loading.value = true;
  selectedS3Keys.value = [];
  try {
    const data = await ApiClient.getRecognitionGalleryImages();
    const generic = toImages(data?.generic?.[props.imageType]);
    const company = toImages(data?.company?.[props.imageType]);
    const byKey = new Map();
    [...generic, ...company].forEach(img => byKey.set(img.s3Key, img));
    images.value = [...byKey.values()];
  } catch {
    images.value = [];
  } finally {
    loading.value = false;
  }
};

const onHidden = () => {
  selectedS3Keys.value = [];
};

const onConfirm = () => {
  const selected = selectedS3Keys.value
    .map(s3Key => images.value.find(img => img.s3Key === s3Key))
    .filter(Boolean)
    .map(img => ({ url: img.url, s3Key: img.s3Key }));
  if (selected.length) {
    emit('select', props.multiple ? selected : selected[0]);
  }
  modal.value?.hide();
};
</script>

<style scoped>
:deep(.modal-title) {
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.5rem;
  width: 100%;
  text-align: center;
  margin-left: 1.25rem;
}

:deep(.modal-header) {
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
}

.gallery-body {
  padding: 1.5rem;
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
  max-height: 60vh;
  overflow-y: auto;
}

.gallery-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 12rem;
}

.gallery-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 12rem;
  color: var(--palette-color-default-lighten-50);
  font-size: 0.875rem;
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
}

.gallery-grid--open-banner {
  grid-template-columns: repeat(2, 1fr);
}

.gallery-item {
  position: relative;
  border: 3px solid transparent;
  border-radius: 0.5rem;
  overflow: hidden;
  cursor: pointer;
  padding: 0;
  background: var(--palette-color-core-1-lighten-90);
  aspect-ratio: 16 / 9;
  transition: border-color 0.15s ease;
}

/* Open-award banners crop to 4:1 in the app, unlike program/milestone banners. */
.gallery-item--open-banner {
  aspect-ratio: 4 / 1;
}

.gallery-item:hover {
  border-color: var(--palette-color-default-lighten-70);
}

.gallery-item:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.gallery-item--selected {
  border-color: var(--palette-color-brand-primary);
}

.gallery-item--selected:hover {
  border-color: var(--palette-color-brand-primary);
}

.gallery-item__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

.gallery-item__tick {
  position: absolute;
  top: 0.375rem;
  right: 0.375rem;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background: var(--palette-color-brand-primary);
  color: var(--palette-color-base-white);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
}
</style>
